import type * as THREE from 'three'
import type { GlbHierarchyNode } from '../../imported-model/loaders/glb'
import {
  completeImportedModelTopologySingleOccurrenceRebindings,
  createImportedModelTopologyOccurrenceRebindings,
} from '../../imported-model/topology/imported-model-topology-runtime'
import {
  collectImportedModelTopologyRepositories,
  type ImportedModelBodyTopologyOccurrenceRebinding,
} from '../../imported-model/topology/ImportedModelTopologyRepository'
import type { ImportedMeshTopologyBinding } from '../../imported-model/topology/ImportedMeshTopologyBinding'
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata'
import { rebaseImportedBodyOccurrenceBindingId } from '../../imported-model/utils/ImportedBodyBindingId'
import type { ModelAssetNodeType } from '../assets/model-asset-types'

/**
 * scoped 项目模型子树 Body occurrence 映射的统一构建输入。
 */
export interface CreateScopedProjectModelBodyOccurrenceRebindingsInput {
  /** 新包组合得到的原始 hierarchy。 */
  readonly generatedHierarchy: GlbHierarchyNode
  /** 按当前项目稳定节点身份重绑后的 hierarchy。 */
  readonly rebasedHierarchy: GlbHierarchyNode
  /** 新包组合得到的运行时场景子树。 */
  readonly rootObject: THREE.Object3D
  /** 当前被替换节点的模型类型。 */
  readonly targetNodeType: ModelAssetNodeType
  /** 当前项目中被替换节点的稳定装配节点 ID。 */
  readonly targetAssemblyNodeId: string
}

/**
 * 判断 topology repository 根是否由当前 detached replacement 子树拥有。
 *
 * @param repositoryRoot repository 自身根对象。
 * @param scopedRoot 当前 replacement 子树根对象。
 * @returns repository 根属于当前子树时返回 `true`。
 */
function isTopologyRepositoryInsideReplacementSubtree(
  repositoryRoot: THREE.Object3D,
  scopedRoot: THREE.Object3D,
): boolean {
  let current: THREE.Object3D | null = repositoryRoot
  while (current) {
    if (current === scopedRoot) {
      return true
    }
    current = current.parent
  }
  return false
}

/**
 * 按相同 Object3D UUID 建立 generated hierarchy 到最终 occurrence 的对象映射。
 *
 * @param rebasedHierarchy 已写入最终项目身份的 hierarchy。
 * @returns hierarchy/selection Object3D UUID 到最终 occurrence 的映射。
 */
function createFinalAssemblyOwnerByHierarchyObjectId(
  rebasedHierarchies: readonly GlbHierarchyNode[],
  rootObjectId: string,
  targetAssemblyNodeId: string,
): ReadonlyMap<string, {
  readonly assemblyNodeId: string
  readonly semanticKind: string
}> {
  const assemblyOwnerByObjectId = new Map<string, {
    readonly assemblyNodeId: string
    readonly semanticKind: string
  }>()
  const visit = (node: GlbHierarchyNode): void => {
    const assemblyNodeId = node.meta?.assemblyNodeId?.trim() ?? ''
    if (assemblyNodeId) {
      const owner = {
        assemblyNodeId,
        semanticKind: node.meta?.semanticKind?.trim() ?? '',
      }
      assemblyOwnerByObjectId.set(node.objectId, owner)
      const selectionObjectId = node.meta?.selectionObjectId?.trim() ?? ''
      if (selectionObjectId) {
        assemblyOwnerByObjectId.set(selectionObjectId, owner)
      }
    }
    node.children.forEach(visit)
  }
  rebasedHierarchies.forEach(visit)
  if (!assemblyOwnerByObjectId.has(rootObjectId)) {
    assemblyOwnerByObjectId.set(rootObjectId, {
      assemblyNodeId: targetAssemblyNodeId,
      semanticKind: 'assembly',
    })
  }
  return assemblyOwnerByObjectId
}

/**
 * 解析 repository Body 在最终 hierarchy 中的唯一对象 owner。
 *
 * 设计说明：visible mesh 可以是 hierarchy 节点的后代，也可以只通过 selection proxy 指向
 * hierarchy 对象；这里只消费这两类显式 Object3D 关系，禁止使用 instance-id、名称或 repository
 * 根推导 occurrence。
 *
 * @param record topology repository Body 记录。
 * @param assemblyNodeIdByObjectId 最终 hierarchy 对象身份索引。
 * @returns 可用于精确约束 repository 重绑定范围的对象 UUID 与最终 occurrence。
 */
function resolveTopologyBodyFinalHierarchyOwner(
  record: ImportedMeshTopologyBinding,
  assemblyOwnerByObjectId: ReadonlyMap<string, {
    readonly assemblyNodeId: string
    readonly semanticKind: string
  }>,
  targetAssemblyNodeId: string,
): { readonly sourceObjectId: string; readonly targetAssemblyNodeId: string } | null {
  /**
   * 第一阶段只沿真实 Object3D 父链寻找最近的 hierarchy owner。
   * 导入 metadata 的 selection proxy 可能仍指向归档包根；若在同一轮遍历中提前消费代理，
   * 就会跳过更近的真实子组件并把多个 Body 全部压到装配根 occurrence。
  */
  let current: THREE.Object3D | null = record.visibleMesh
  let physicalAssemblyFallback: {
    readonly sourceObjectId: string
    readonly targetAssemblyNodeId: string
  } | null = null
  while (current) {
    const directOwner = assemblyOwnerByObjectId.get(current.uuid)
    if (directOwner) {
      const candidate = {
        sourceObjectId: current.uuid,
        targetAssemblyNodeId: directOwner.assemblyNodeId,
      }
      /**
       * 装配 hierarchy 节点是场景/仓库容器，不是其后代 Body 的零件 owner。
       * 目标根即使旧数据误标为 part，也仍是本次 assembly replacement 的容器，
       * 因而只作为最后兜底；真实 part/body 祖先则立即胜出。
       */
      if (
        directOwner.assemblyNodeId !== targetAssemblyNodeId
        && directOwner.semanticKind !== 'assembly'
      ) {
        return candidate
      }
      if (!physicalAssemblyFallback) {
        physicalAssemblyFallback = candidate
      }
    }
    current = current.parent
  }

  /**
   * 第二阶段仅在物理父链没有任何 hierarchy owner 时使用显式选择代理兜底。
   * 代理只提供 Object3D UUID 到 occurrence 的明确关系，不参与名称或局部 instance-id 推导。
   */
  current = record.visibleMesh
  while (current) {
    const metadata = ImportedModelObjectMetadataService.readOwnMetadata(current)
    const proxyObjectIds = [
      metadata?.selectionProxyObjectId,
      metadata?.hierarchy?.meta?.selectionObjectId,
    ]
      .map((objectId) => objectId?.trim() ?? '')
      .filter((objectId) => objectId.length > 0)
    const matchedProxyObjectId = proxyObjectIds.find((objectId) => (
      assemblyOwnerByObjectId.has(objectId)
    ))
    if (matchedProxyObjectId) {
      const targetOwner = assemblyOwnerByObjectId.get(matchedProxyObjectId)
      if (!targetOwner) {
        return null
      }
      return {
        sourceObjectId: matchedProxyObjectId,
        targetAssemblyNodeId: targetOwner.assemblyNodeId,
      }
    }
    current = current.parent
  }
  return physicalAssemblyFallback
}

/**
 * 为项目装配体中未进入 hierarchy bodyEntries 的 repository Body 补齐 occurrence 映射。
 *
 * 同一入口服务首次组合与局部切换；只依赖当前子树、最终 hierarchy
 * 和明确 Object3D 父链，不使用名称或局部 instance ID 猜测归属。
 *
 * @param input 项目装配子树、最终 hierarchy 与已有明确映射。
 * @returns 保留 hierarchy 映射并追加可由 Object3D 关系唯一定位的 Body 映射。
 */
export function completeProjectModelAssemblyTopologyBodyOccurrenceRebindings(input: {
  readonly rootObject: THREE.Object3D
  readonly rebasedHierarchies: readonly GlbHierarchyNode[]
  readonly targetAssemblyNodeId: string
  readonly existingRebindings: readonly ImportedModelBodyTopologyOccurrenceRebinding[]
}): ImportedModelBodyTopologyOccurrenceRebinding[] {
  const completed = input.existingRebindings.map((rebinding) => ({ ...rebinding }))
  const unscopedSourceBodyBindingIds = new Set(
    completed
      .filter((rebinding) => !rebinding.sourceObjectId)
      .map((rebinding) => rebinding.sourceBodyBindingId),
  )
  const scopedSourceBodyBindingKeys = new Set(
    completed
      .filter((rebinding) => rebinding.sourceObjectId)
      .map((rebinding) => (
        `${rebinding.sourceObjectId}\u0000${rebinding.sourceBodyBindingId}`
      )),
  )
  const assemblyOwnerByObjectId = createFinalAssemblyOwnerByHierarchyObjectId(
    input.rebasedHierarchies,
    input.rootObject.uuid,
    input.targetAssemblyNodeId,
  )

  collectImportedModelTopologyRepositories(input.rootObject).forEach((repository) => {
    if (!isTopologyRepositoryInsideReplacementSubtree(repository.getRoot(), input.rootObject)) {
      return
    }

    repository.listBodyRecords().forEach((record) => {
      const owner = resolveTopologyBodyFinalHierarchyOwner(
        record,
        assemblyOwnerByObjectId,
        input.targetAssemblyNodeId,
      )
      if (!owner) {
        return
      }
      const scopedSourceKey = `${owner.sourceObjectId}\u0000${record.bodyBindingId}`
      if (
        unscopedSourceBodyBindingIds.has(record.bodyBindingId)
        || scopedSourceBodyBindingKeys.has(scopedSourceKey)
      ) {
        return
      }
      completed.push({
        sourceObjectId: owner.sourceObjectId,
        sourceBodyBindingId: record.bodyBindingId,
        targetBodyBindingId: rebaseImportedBodyOccurrenceBindingId(
          record.bodyBindingId,
          owner.targetAssemblyNodeId,
        ),
        targetAssemblyNodeId: owner.targetAssemblyNodeId,
      })
      scopedSourceBodyBindingKeys.add(scopedSourceKey)
    })
  })

  return completed
}

/**
 * 创建 scoped 项目模型子树的完整 Body occurrence 映射。
 *
 * 设计说明：hierarchy 是导入 Body 身份的首要明确来源；一级零件还可能包含未进入 hierarchy、
 * 但已经注册到 topology repository 的 Feature Body，因此由 cad-core 在同一入口内完成补齐。
 * 装配体可能包含多个独立 occurrence，repository Body 只能通过 Object3D/selection proxy 的显式
 * 对象作用域补齐，禁止用切换根节点身份覆盖整个子树。
 * Undo/Redo 说明：本函数只计算切换事务提交前的派生身份映射，不直接修改 CAD 主状态，
 * 历史记录仍由外层节点切换事务统一管理。
 *
 * @param input 原始/重绑 hierarchy、运行时子树及目标节点上下文。
 * @returns 可供 metadata、topology repository 与 Mate 引用共同消费的唯一映射。
 */
export function createScopedProjectModelBodyOccurrenceRebindings(
  input: CreateScopedProjectModelBodyOccurrenceRebindingsInput,
): ImportedModelBodyTopologyOccurrenceRebinding[] {
  const hierarchyRebindings = createImportedModelTopologyOccurrenceRebindings({
    sourceRoots: [input.generatedHierarchy],
    targetRoots: [input.rebasedHierarchy],
    conflictContextLabel: '项目模型局部替换',
  })

  return input.targetNodeType === 'part'
    ? completeImportedModelTopologySingleOccurrenceRebindings({
        root: input.rootObject,
        targetAssemblyNodeId: input.targetAssemblyNodeId,
        existingRebindings: hierarchyRebindings,
      })
    : completeProjectModelAssemblyTopologyBodyOccurrenceRebindings({
        rootObject: input.rootObject,
        rebasedHierarchies: [input.rebasedHierarchy],
        targetAssemblyNodeId: input.targetAssemblyNodeId,
        existingRebindings: hierarchyRebindings,
      })
}
